import { useCallback, useEffect, useRef, useState } from "react";
import { Box } from "@mui/material";
import cockroachGameService from "../api/cockroachGameService";

const ROUND_SECONDS = 30;
const MAX_BUGS = 6;
const BASE_SIZE = 66;
const MIN_SIZE = 50;
const BASE_SPEED = 22;
const MAX_SPEED = 88;
const COMBO_MS = 900;
const HIT_PAD = 20;

const STYLES = `
  .crg-root {
    width: 100%;
    flex-shrink: 0;
    display: flex;
    flex-direction: column;
    background: #ffffff;
    border-radius: 16px;
    border: 1.5px solid #e4e7ed;
    overflow: hidden;
    box-shadow: 0 4px 24px rgba(15,17,23,0.06), 0 1px 4px rgba(15,17,23,0.04);
    user-select: none;
    -webkit-user-select: none;
    font-family: system-ui, -apple-system, "Segoe UI", sans-serif;
  }
  .crg-bar {
    display: flex;
    align-items: baseline;
    justify-content: space-between;
    gap: 10px;
    padding: 10px 12px 8px;
    border-bottom: 1.5px solid #f0f2f6;
    background: linear-gradient(180deg, #fafbfd 0%, #ffffff 100%);
  }
  .crg-hint {
    font-size: 12px;
    font-weight: 600;
    color: #0f1117;
    letter-spacing: -0.01em;
  }
  .crg-scores {
    display: flex;
    align-items: baseline;
    gap: 8px;
    flex-shrink: 0;
    font-variant-numeric: tabular-nums;
  }
  .crg-score { font-size: 13px; font-weight: 700; color: #0f1117; }
  .crg-time {
    font-size: 12px;
    font-weight: 700;
    color: #1975d1;
    min-width: 28px;
    text-align: right;
  }
  .crg-time.is-urgent { color: #dc2626; }
  .crg-arena {
    position: relative;
    height: 210px;
    overflow: hidden;
    touch-action: manipulation;
    cursor: pointer;
    background:
      radial-gradient(circle at 20% 30%, rgba(255,255,255,0.7) 0 18%, transparent 19%),
      radial-gradient(circle at 78% 62%, rgba(255,255,255,0.55) 0 14%, transparent 15%),
      repeating-linear-gradient(90deg, rgba(228,231,237,0.55) 0 1px, transparent 1px 22px),
      repeating-linear-gradient(0deg, rgba(228,231,237,0.55) 0 1px, transparent 1px 22px),
      #f4f1ea;
  }
  .crg-arena.is-urgent {
    box-shadow: inset 0 0 0 3px rgba(220, 38, 38, 0.45);
    animation: crg-pulse 0.55s ease-in-out infinite;
  }
  .crg-timer {
    position: absolute;
    left: 0; right: 0; top: 0;
    height: 8px;
    background: rgba(15,17,23,0.08);
    z-index: 3;
    pointer-events: none;
  }
  .crg-timer-fill {
    height: 100%;
    transform-origin: left center;
    background: linear-gradient(90deg, #22c55e, #eab308 70%, #ef4444);
    transition: width 80ms linear;
  }
  .crg-bug {
    position: absolute;
    left: 0; top: 0;
    pointer-events: none;
    will-change: transform;
    transform-origin: center center;
  }
  .crg-bug.is-hot svg {
    filter: drop-shadow(0 0 8px rgba(25, 117, 209, 0.55));
  }
  .crg-bug svg {
    display: block;
    filter: drop-shadow(0 1px 1.5px rgba(30, 18, 8, 0.4));
  }
  .crg-splat, .crg-pop { position: absolute; pointer-events: none; z-index: 2; }
  .crg-splat {
    border-radius: 50%;
    background: radial-gradient(circle, rgba(92, 52, 18, 0.45) 0 35%, rgba(92, 52, 18, 0) 70%);
    animation: crg-fade 420ms ease-out forwards;
  }
  .crg-pop {
    font-size: 13px;
    font-weight: 700;
    color: #16a34a;
    text-shadow: 0 1px 0 rgba(255,255,255,0.8);
    animation: crg-pop 520ms ease-out forwards;
  }
  .crg-overlay {
    position: absolute;
    inset: 0;
    z-index: 5;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 8px;
    background: rgba(15, 17, 23, 0.82);
    color: #fff;
    text-align: center;
    padding: 16px;
    animation: crg-over-in 420ms cubic-bezier(0.22, 1, 0.36, 1);
  }
  .crg-over-kicker {
    font-size: 11px;
    font-weight: 700;
    letter-spacing: 0.16em;
    text-transform: uppercase;
    color: #fbbf24;
  }
  .crg-over-title {
    font-size: 34px;
    font-weight: 800;
    letter-spacing: -0.04em;
    line-height: 1;
  }
  .crg-over-score {
    font-size: 28px;
    font-weight: 800;
    font-variant-numeric: tabular-nums;
  }
  .crg-over-note { font-size: 12px; color: rgba(255,255,255,0.72); }
  .crg-over-btn {
    margin-top: 6px;
    border: 0;
    border-radius: 10px;
    padding: 8px 14px;
    font-size: 12px;
    font-weight: 700;
    background: #1975d1;
    color: #fff;
    cursor: pointer;
  }
  .crg-over-btn:hover { background: #1565c0; }
  .crg-countdown {
    position: absolute;
    inset: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 64px;
    font-weight: 800;
    color: rgba(220, 38, 38, 0.28);
    pointer-events: none;
    z-index: 2;
  }
  @keyframes crg-fade {
    0% { opacity: 1; transform: translate(-50%, -50%) scale(0.6); }
    100% { opacity: 0; transform: translate(-50%, -50%) scale(1.35); }
  }
  @keyframes crg-pop {
    0% { opacity: 1; transform: translate(-50%, -50%); }
    100% { opacity: 0; transform: translate(-50%, -110%); }
  }
  @keyframes crg-pulse {
    0%, 100% { box-shadow: inset 0 0 0 3px rgba(220, 38, 38, 0.35); }
    50% { box-shadow: inset 0 0 0 5px rgba(220, 38, 38, 0.7); }
  }
  @keyframes crg-over-in {
    from { opacity: 0; transform: scale(1.04); }
    to { opacity: 1; transform: scale(1); }
  }
`;

let nextId = 1;

function rand(min, max) {
  return min + Math.random() * (max - min);
}

function createBug(w, h, kills, fromEdge) {
  const t = kills / (kills + 14);
  const size = BASE_SIZE - t * (BASE_SIZE - MIN_SIZE);
  const speed = BASE_SPEED + t * (MAX_SPEED - BASE_SPEED);
  const m = size * 0.5 + 2;
  let x;
  let y;
  let angle;

  if (fromEdge && w > 0 && h > 0) {
    const side = Math.floor(Math.random() * 4);
    if (side === 0) {
      x = rand(m, w - m); y = m; angle = rand(0.35, Math.PI - 0.35);
    } else if (side === 1) {
      x = w - m; y = rand(m, h - m); angle = rand(Math.PI * 0.6, Math.PI * 1.4);
    } else if (side === 2) {
      x = rand(m, w - m); y = h - m; angle = -rand(0.35, Math.PI - 0.35);
    } else {
      x = m; y = rand(m, h - m); angle = rand(-Math.PI * 0.4, Math.PI * 0.4);
    }
  } else {
    x = w * 0.5;
    y = h * 0.5;
    angle = rand(0, Math.PI * 2);
  }

  return {
    id: nextId++,
    x, y, size, speed,
    vx: Math.cos(angle) * speed,
    vy: Math.sin(angle) * speed,
    panic: 0,
    hover: false,
  };
}

function CockroachMark({ size }) {
  return (
    <svg width={size} height={size} viewBox="0 0 64 64" aria-hidden>
      <g fill="none" strokeLinecap="round" strokeLinejoin="round">
        <path d="M28 16 C22 6 18 4 16 5" stroke="#2a1a0c" strokeWidth="1.7" />
        <path d="M36 16 C42 6 46 4 48 5" stroke="#2a1a0c" strokeWidth="1.7" />
        <path d="M22 28 C8 24 6 18 7 16" stroke="#2a1a0c" strokeWidth="1.9" />
        <path d="M42 28 C56 24 58 18 57 16" stroke="#2a1a0c" strokeWidth="1.9" />
        <path d="M20 36 C6 38 4 42 5 46" stroke="#2a1a0c" strokeWidth="1.9" />
        <path d="M44 36 C58 38 60 42 59 46" stroke="#2a1a0c" strokeWidth="1.9" />
        <path d="M22 46 C10 54 8 58 10 60" stroke="#2a1a0c" strokeWidth="1.9" />
        <path d="M42 46 C54 54 56 58 54 60" stroke="#2a1a0c" strokeWidth="1.9" />
        <ellipse cx="32" cy="46" rx="11.5" ry="14.5" fill="#6b4423" />
        <path d="M24 42 H40 M23.5 48 H40.5 M25 54 H39" stroke="#4a2e14" strokeWidth="1.15" opacity="0.7" />
        <ellipse cx="32" cy="30" rx="13" ry="9.5" fill="#5c3d1e" />
        <ellipse cx="32" cy="19.5" rx="6.2" ry="6.8" fill="#3d2914" />
        <circle cx="29.6" cy="18.2" r="1.15" fill="#1a0e08" />
        <circle cx="34.4" cy="18.2" r="1.15" fill="#1a0e08" />
        <path d="M27 58 C29 62 31 62 32 61" stroke="#2a1a0c" strokeWidth="1.5" />
        <path d="M37 58 C35 62 33 62 32 61" stroke="#2a1a0c" strokeWidth="1.5" />
      </g>
    </svg>
  );
}

export default function CockroachGame({ onGameEnd }) {
  const arenaRef = useRef(null);
  const bugsRef = useRef([]);
  const sizeRef = useRef({ w: 320, h: 210 });
  const phaseRef = useRef("idle");
  const timeRef = useRef(ROUND_SECONDS);
  const killsRef = useRef(0);
  const comboRef = useRef(0);
  const lastKillRef = useRef(0);
  const scoreRef = useRef(0);
  const hoverIdRef = useRef(null);
  const lastHitRef = useRef({ id: null, at: 0 });
  const endingRef = useRef(false);
  const onGameEndRef = useRef(onGameEnd);
  const fxIdRef = useRef(1);

  const [score, setScore] = useState(0);
  const [timeLeft, setTimeLeft] = useState(ROUND_SECONDS);
  const [phase, setPhase] = useState("idle");
  const [fx, setFx] = useState([]);
  const [isNewBest, setIsNewBest] = useState(false);
  const [rank, setRank] = useState(null);
  const [, setRev] = useState(0);

  onGameEndRef.current = onGameEnd;

  const syncBugs = useCallback(() => setRev((n) => n + 1), []);

  const spawnInitial = useCallback(() => {
    const { w, h } = sizeRef.current;
    bugsRef.current = [createBug(w, h, 0, false)];
    syncBugs();
  }, [syncBugs]);

  const finishRound = useCallback(async () => {
    if (endingRef.current) return;
    endingRef.current = true;
    phaseRef.current = "over";
    timeRef.current = 0;
    setTimeLeft(0);
    setPhase("over");
    bugsRef.current.forEach((bug) => {
      bug.speed = 0;
      bug.vx = 0;
      bug.vy = 0;
    });
    syncBugs();

    const finalScore = scoreRef.current;
    try {
      const res = await cockroachGameService.submitScore(finalScore);
      const data = res.data?.data || {};
      setIsNewBest(Boolean(data.isNewBest));
      setRank(data.rank || null);
      onGameEndRef.current?.(data);
    } catch {
      setIsNewBest(false);
      setRank(null);
      onGameEndRef.current?.({ lastScore: finalScore, leaderboard: [] });
    }
  }, [syncBugs]);

  useEffect(() => {
    const arena = arenaRef.current;
    if (!arena) return undefined;

    const measure = () => {
      const rect = arena.getBoundingClientRect();
      if (rect.width > 8 && rect.height > 8) {
        sizeRef.current = { w: rect.width, h: rect.height };
      }
    };

    measure();
    if (bugsRef.current.length === 0) spawnInitial();

    let last = performance.now();
    let raf = 0;

    const loop = (now) => {
      const dt = Math.min(0.05, (now - last) / 1000);
      last = now;
      const { w, h } = sizeRef.current;

      if (phaseRef.current === "playing") {
        timeRef.current = Math.max(0, timeRef.current - dt);
        const shown = Math.ceil(timeRef.current);
        setTimeLeft((prev) => (prev !== shown ? shown : prev));
        const fill = arena.querySelector(".crg-timer-fill");
        if (fill) fill.style.width = `${(timeRef.current / ROUND_SECONDS) * 100}%`;
        if (timeRef.current <= 0) {
          finishRound();
        }
      }

      if (phaseRef.current !== "over") {
        for (const bug of bugsRef.current) {
          bug.hover = hoverIdRef.current === bug.id;
          if (bug.panic > 0) bug.panic = Math.max(0, bug.panic - dt);
          const spd = bug.speed * (bug.panic > 0 ? 2 : 1) * (bug.hover ? 0.22 : 1);
          const ang = Math.atan2(bug.vy, bug.vx);
          bug.vx = Math.cos(ang) * spd;
          bug.vy = Math.sin(ang) * spd;

          if (!bug.hover && Math.random() < 0.016) {
            const next = ang + (Math.random() - 0.5) * 1.05;
            bug.vx = Math.cos(next) * spd;
            bug.vy = Math.sin(next) * spd;
          }

          bug.x += bug.vx * dt;
          bug.y += bug.vy * dt;
          const m = bug.size * 0.5 + 1;
          if (bug.x < m) { bug.x = m; bug.vx = Math.abs(bug.vx); }
          else if (bug.x > w - m) { bug.x = w - m; bug.vx = -Math.abs(bug.vx); }
          if (bug.y < m + 8) { bug.y = m + 8; bug.vy = Math.abs(bug.vy); }
          else if (bug.y > h - m) { bug.y = h - m; bug.vy = -Math.abs(bug.vy); }

          const el = arena.querySelector(`[data-bug="${bug.id}"]`);
          if (el) {
            const facing = Math.atan2(bug.vy, bug.vx) + Math.PI / 2;
            el.style.transform = `translate3d(${bug.x - bug.size / 2}px, ${bug.y - bug.size / 2}px, 0) rotate(${facing}rad)`;
            el.classList.toggle("is-hot", bug.hover);
          }
        }
      }

      raf = requestAnimationFrame(loop);
    };

    raf = requestAnimationFrame(loop);
    const ro = new ResizeObserver(measure);
    ro.observe(arena);
    return () => {
      cancelAnimationFrame(raf);
      ro.disconnect();
    };
  }, [finishRound, spawnInitial]);

  const hitTest = (clientX, clientY) => {
    const arena = arenaRef.current;
    if (!arena) return null;
    const rect = arena.getBoundingClientRect();
    const x = clientX - rect.left;
    const y = clientY - rect.top;
    let best = null;
    let bestDist = Infinity;
    for (const bug of bugsRef.current) {
      const dx = x - bug.x;
      const dy = y - bug.y;
      const r = bug.size / 2 + HIT_PAD;
      const d = dx * dx + dy * dy;
      if (d <= r * r && d < bestDist) {
        best = bug;
        bestDist = d;
      }
    }
    return best;
  };

  const squash = (id) => {
    if (phaseRef.current === "over") return;
    const now = performance.now();
    if (lastHitRef.current.id === id && now - lastHitRef.current.at < 180) return;
    const bug = bugsRef.current.find((item) => item.id === id);
    if (!bug) return;
    lastHitRef.current = { id, at: now };

    if (phaseRef.current === "idle") {
      phaseRef.current = "playing";
      timeRef.current = ROUND_SECONDS;
      setPhase("playing");
      setTimeLeft(ROUND_SECONDS);
    }

    comboRef.current = now - lastKillRef.current < COMBO_MS ? comboRef.current + 1 : 0;
    lastKillRef.current = now;
    killsRef.current += 1;
    const points = 10 + comboRef.current * 5;
    scoreRef.current += points;
    setScore(scoreRef.current);

    const splatId = fxIdRef.current++;
    const popId = fxIdRef.current++;
    setFx((prev) => [
      ...prev,
      { id: splatId, type: "splat", x: bug.x, y: bug.y, size: bug.size },
      { id: popId, type: "pop", x: bug.x, y: bug.y, points },
    ]);
    window.setTimeout(() => {
      setFx((prev) => prev.filter((item) => item.id !== splatId && item.id !== popId));
    }, 520);

    const remaining = bugsRef.current.filter((item) => item.id !== id);
    remaining.forEach((item) => {
      item.panic = 0.32;
      const ang = rand(0, Math.PI * 2);
      const spd = item.speed * 2;
      item.vx = Math.cos(ang) * spd;
      item.vy = Math.sin(ang) * spd;
    });

    const { w, h } = sizeRef.current;
    const room = Math.max(0, MAX_BUGS - remaining.length);
    const spawnCount = Math.max(1, Math.min(2, room || 1));
    const next = [...remaining];
    for (let i = 0; i < spawnCount && next.length < MAX_BUGS; i += 1) {
      next.push(createBug(w, h, killsRef.current, true));
    }
    if (next.length === 0) next.push(createBug(w, h, killsRef.current, true));
    bugsRef.current = next;
    syncBugs();
  };

  const onArenaPointer = (event) => {
    if (phaseRef.current === "over") return;
    if (typeof event.button === "number" && event.button > 0) return;
    event.preventDefault();
    const bug = hitTest(event.clientX, event.clientY);
    if (bug) squash(bug.id);
  };

  const onArenaMove = (event) => {
    if (phaseRef.current === "over") return;
    const bug = hitTest(event.clientX, event.clientY);
    hoverIdRef.current = bug?.id ?? null;
  };

  const restart = () => {
    endingRef.current = false;
    phaseRef.current = "idle";
    timeRef.current = ROUND_SECONDS;
    killsRef.current = 0;
    comboRef.current = 0;
    lastKillRef.current = 0;
    scoreRef.current = 0;
    hoverIdRef.current = null;
    setScore(0);
    setTimeLeft(ROUND_SECONDS);
    setPhase("idle");
    setFx([]);
    setIsNewBest(false);
    setRank(null);
    spawnInitial();
  };

  const urgent = phase === "playing" && timeLeft <= 5;
  const timerPct = phase === "playing" ? (timeRef.current / ROUND_SECONDS) * 100 : 100;

  return (
    <Box className="crg-root" component="aside">
      <style>{STYLES}</style>
      <div className="crg-bar">
        <span className="crg-hint">
          {phase === "over" ? "Ronda terminada" : "Click en la cucaracha"}
        </span>
        <div className="crg-scores">
          <span className="crg-score">{score}</span>
          {phase === "playing" && (
            <span className={`crg-time${urgent ? " is-urgent" : ""}`}>{timeLeft}s</span>
          )}
        </div>
      </div>
      <div
        ref={arenaRef}
        className={`crg-arena${urgent ? " is-urgent" : ""}`}
        onPointerDown={onArenaPointer}
        onPointerMove={onArenaMove}
        onPointerLeave={() => { hoverIdRef.current = null; }}
        onContextMenu={(event) => event.preventDefault()}
      >
        {phase !== "idle" && (
          <div className="crg-timer">
            <div className="crg-timer-fill" style={{ width: `${timerPct}%` }} />
          </div>
        )}
        {urgent && <div className="crg-countdown">{timeLeft}</div>}
        {fx.map((item) =>
          item.type === "splat" ? (
            <span
              key={item.id}
              className="crg-splat"
              style={{ left: item.x, top: item.y, width: item.size, height: item.size * 0.7 }}
            />
          ) : (
            <span key={item.id} className="crg-pop" style={{ left: item.x, top: item.y }}>
              +{item.points}
            </span>
          )
        )}
        {phase !== "over" &&
          bugsRef.current.map((bug) => {
            const angle = Math.atan2(bug.vy, bug.vx) + Math.PI / 2;
            return (
              <div
                key={bug.id}
                data-bug={bug.id}
                className="crg-bug"
                style={{
                  width: bug.size,
                  height: bug.size,
                  transform: `translate3d(${bug.x - bug.size / 2}px, ${bug.y - bug.size / 2}px, 0) rotate(${angle}rad)`,
                }}
              >
                <CockroachMark size={bug.size} />
              </div>
            );
          })}
        {phase === "over" && (
          <div className="crg-overlay">
            <div className="crg-over-kicker">Se acabó el tiempo</div>
            <div className="crg-over-title">FIN</div>
            <div className="crg-over-score">{score} pts</div>
            <div className="crg-over-note">
              {isNewBest ? "Nuevo récord personal" : rank ? `Quedaste en el puesto ${rank}` : "Mira el ranking abajo"}
            </div>
            <button type="button" className="crg-over-btn" onClick={restart}>
              Jugar de nuevo
            </button>
          </div>
        )}
      </div>
    </Box>
  );
}
